/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import * as echarts from 'echarts';
import type { StackByOption } from './FilterControls';
import { IncidentFilterBar, FilterDim } from './IncidentFilterBar';
import { ViewModeToggle, ViewMode } from './ViewModeToggle';
import { DocumentListModal, DocumentListItem, ActiveFilter } from './DocumentListModal';
import { DocumentDetailModal, DocumentDetail } from './DocumentDetailModal';
import { SummaryModal, SummarySeriesItem } from './SummaryModal';
import { getCoverageColor, getCategoryColor, getActorRoleColor, getChartTheme, detectDarkMode, getResponsiveConfig, loadTheme } from '@/lib/theme';
import { getSubdomainName, getSubdomainDescription, getSubdomainInfo } from '@/data/riskTaxonomy';
import { bindChartTap } from '@/lib/chartTap';
import { documentUrl } from '@/lib/documentUrl';
import { useModalUrl } from './useModalUrl';
import { SECTOR_FIELDS, ACTOR_ROLES, DEFAULT_ACTOR_ROLE, RISK_DOMAINS } from '@/data/chartConfigs';
import type { ChartConfig, ActorRole } from '@/data/chartConfigs';

export interface CategoryBarChartProps {
  config: ChartConfig;
  apiBase: string;
  containerId: string;
  initialFilters?: Record<string, string>;
  initialStackBy?: StackByOption;
  initialViewMode?: ViewMode;
}

// Global registry for external filter control
export interface ChartController {
  setStackBy: (value: StackByOption) => void;
  setCoverageFilter: (value: string | string[]) => void;
  setJurisdictionFilter: (value: string | string[]) => void;
  setAuthorityFilter: (value: string | string[]) => void;
  setLegislativeStatusFilter: (value: string | string[]) => void;
  setActivityFilter: (value: string | string[]) => void;
  setAppliesToFilter: (value: string | string[]) => void;
  setLifecycleStageFilter: (value: string | string[]) => void;
  setTechnicalScopeFilter: (value: string | string[]) => void;
  setRiskSubdomainFilter: (value: string | string[]) => void;
  setRiskDomainFilter: (value: string | string[]) => void;
  setSectorFilter: (value: string | string[]) => void;
  setViewMode: (value: ViewMode) => void;
  setActorRole: (value: ActorRole) => void;
  resetFilters: () => void;
}

const normalizeFilter = (v: string | string[]): string[] =>
  Array.isArray(v) ? v : v ? [v] : [];

const chartRegistry = new Map<string, ChartController>();

export function getChartController(id: string): ChartController | undefined {
  return chartRegistry.get(id);
}

interface FilterOption {
  value: string;
  label: string;
}

interface DataRow {
  id: string;
  title: string;
  values: number[];
}

interface ApiResponse {
  seriesCategories: string[];
  data: DataRow[];
  totalDocumentsAll: number;
  jurisdictions?: string[];
  authorities?: string[];
  legislativeStatuses?: string[];
  activities?: string[];
  appliesToOptions?: string[];
  lifecycleStageOptions?: string[];
  technicalScopeOptions?: string[];
}

// Legend heading labels. No trailing colon: these render as a bold
// heading on their own line (matching the incident charts' LegendHeader),
// not as an inline "label: value" prefix the way they used to.
const STACK_BY_LABELS: Record<StackByOption, string> = {
  none: 'Documents',
  coverage: 'Level of coverage',
  jurisdiction: 'Jurisdiction',
  authority: 'Authority',
  legislativeStatus: 'Legislative status',
  activity: 'Most recent activity',
  actorRole: 'Actor role',
};

const COVERAGE_BY_SERIES: Record<string, number> = {
  'Good coverage': 3,
  'Minimal coverage': 2,
  'No coverage': 1,
};

const ROW_HEIGHT = 25.5;
const EXPORT_ROW_HEIGHT = 32;
const MOBILE_BREAKPOINT = 640;

/** Detect export mode flag set by the export page */
function isExportMode(): boolean {
  return typeof window !== 'undefined' && !!(window as any).__EXPORT_MODE__;
}

const COVERAGE_LEGEND_TOOLTIPS: Record<string, string> = {
  'Good coverage': 'Good Coverage: Comprehensive governance measures or mitigations with clear procedures relating to the risk subdomain are explicitly described in the document.',
  'Minimal coverage': 'Minimal Coverage: Brief mention but no specific mitigations, recommendations or governance measures relating to the risk subdomain are described.',
  'No coverage': 'No Coverage.',
};

// Details filter param name mapping. The clicked-bar ID is sent under these
// query-string keys; for riskDomain we use `riskDomainId` (not `riskDomain`)
// so it doesn't collide with the cross-cutting `riskDomain` filter that
// parallels `riskSubdomain`.
const DETAILS_FILTER_PARAMS: Record<string, string> = {
  subdomain: 'subdomain',
  riskDomain: 'riskDomainId',
  sector: 'sector',
  lifecycle: 'lifecycleValue',
  technicalScope: 'technicalScopeValue',
  legislativeStatus: 'legislativeStatusValue',
  actors: 'actorValue',
};

// Static filter options for cross-cutting filters
const SUBDOMAIN_IDS = [
  '1.1','1.2','1.3','2.1','2.2','3.1','3.2','4.1','4.2','4.3',
  '5.1','5.2','6.1','6.2','6.3','6.4','6.5','6.6','7.1','7.2','7.3','7.4','7.5','7.6',
];

const RISK_SUBDOMAIN_OPTIONS: FilterOption[] = SUBDOMAIN_IDS.map((id) => ({
  value: id,
  label: `${id} - ${getSubdomainInfo(id)?.name ?? id}`,
}));

const RISK_DOMAIN_OPTIONS: FilterOption[] = RISK_DOMAINS.map((d) => ({
  value: d.id,
  label: `${d.id} - ${d.label}`,
}));

const SECTOR_OPTIONS: FilterOption[] = SECTOR_FIELDS.map((sf) => ({
  value: sf.id,
  label: sf.label,
}));

const COVERAGE_OPTIONS: FilterOption[] = [
  { value: '3', label: 'Good coverage' },
  { value: '2', label: 'Minimal coverage' },
  { value: '1', label: 'No coverage' },
];

const STACK_BY_DISPLAY: Record<StackByOption, string> = {
  none: 'None',
  coverage: 'Coverage',
  jurisdiction: 'Jurisdiction',
  authority: 'Authority',
  legislativeStatus: 'Legislative status',
  activity: 'Most recent activity',
  actorRole: 'Actor role',
};

function niceMax(value: number): number {
  if (value <= 0) return 1;
  // Compute a nice tick interval targeting ~5 ticks, then round up
  const rawInterval = value / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const normalized = rawInterval / magnitude;
  let tickInterval: number;
  if (normalized <= 1.5) tickInterval = magnitude;
  else if (normalized <= 3.5) tickInterval = 2 * magnitude;
  else if (normalized <= 7.5) tickInterval = 5 * magnitude;
  else tickInterval = 10 * magnitude;
  return Math.ceil(value / tickInterval) * tickInterval;
}

export function CategoryBarChart({
  config,
  apiBase,
  containerId,
  initialFilters = {},
  initialStackBy,
  initialViewMode,
}: CategoryBarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  // Detaches the tap/click listeners bound by bindChartTap.
  const tapCleanupRef = useRef<(() => void) | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const theme = loadTheme();

  const defaultStackBy = initialStackBy || config.defaultStackBy;
  const defaultViewMode = initialViewMode || (config.defaultViewMode as ViewMode);

  // State
  const [stackBy, setStackBy] = useState<StackByOption>(defaultStackBy);
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);
  const [coverageFilter, setCoverageFilter] = useState<string[]>(
    initialFilters.coverage ? [initialFilters.coverage] : []
  );
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string[]>(
    initialFilters.jurisdiction ? [initialFilters.jurisdiction] : []
  );
  const [authorityFilter, setAuthorityFilter] = useState<string[]>(
    initialFilters.authority ? [initialFilters.authority] : []
  );
  const [legislativeStatusFilter, setLegislativeStatusFilter] = useState<string[]>(
    initialFilters.legislativeStatus ? [initialFilters.legislativeStatus] : []
  );
  const [activityFilter, setActivityFilter] = useState<string[]>(
    initialFilters.activity ? [initialFilters.activity] : []
  );
  const [appliesToFilter, setAppliesToFilter] = useState<string[]>(
    initialFilters.appliesTo ? [initialFilters.appliesTo] : []
  );
  const [lifecycleStageFilter, setLifecycleStageFilter] = useState<string[]>(
    initialFilters.lifecycleStage ? [initialFilters.lifecycleStage] : []
  );
  const [technicalScopeFilter, setTechnicalScopeFilter] = useState<string[]>(
    initialFilters.technicalScope ? [initialFilters.technicalScope] : []
  );
  const [riskSubdomainFilter, setRiskSubdomainFilter] = useState<string[]>(
    initialFilters.riskSubdomain ? [initialFilters.riskSubdomain] : []
  );
  const [riskDomainFilter, setRiskDomainFilter] = useState<string[]>(
    initialFilters.riskDomain ? [initialFilters.riskDomain] : []
  );
  const [sectorFilter, setSectorFilter] = useState<string[]>(
    initialFilters.sector ? [initialFilters.sector] : []
  );
  const [actorRole, setActorRole] = useState<ActorRole>(
    (initialFilters.actorRole as ActorRole) || DEFAULT_ACTOR_ROLE
  );

  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(detectDarkMode());
  const [isMobile, setIsMobile] = useState(false);

  // Filter options from API
  const [jurisdictions, setJurisdictions] = useState<FilterOption[]>([]);
  const [authorities, setAuthorities] = useState<FilterOption[]>([]);
  const [legislativeStatuses, setLegislativeStatuses] = useState<FilterOption[]>([]);
  const [activities, setActivities] = useState<FilterOption[]>([]);
  const [appliesToOptions, setAppliesToOptions] = useState<FilterOption[]>([]);
  const [lifecycleStageOptions, setLifecycleStageOptions] = useState<FilterOption[]>([]);
  const [technicalScopeOptions, setTechnicalScopeOptions] = useState<FilterOption[]>([]);

  // Document list modal state
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listModalTitle, setListModalTitle] = useState('');
  const [listModalItems, setListModalItems] = useState<DocumentListItem[]>([]);
  const [listModalLoading, setListModalLoading] = useState(false);
  const [listModalFilters, setListModalFilters] = useState<ActiveFilter[]>([]);

  // Document detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailDocumentId, setDetailDocumentId] = useState('');
  const [detailDocument, setDetailDocument] = useState<DocumentDetail | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);

  // Mobile summary modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryModalTitle, setSummaryModalTitle] = useState('');
  const [summaryModalSeries, setSummaryModalSeries] = useState<SummarySeriesItem[]>([]);
  const pendingClickRef = useRef<{ params: echarts.ECElementEvent; ids: string[]; labels: string[] } | null>(null);
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  // Register controller for external access
  useEffect(() => {
    const controller: ChartController = {
      setStackBy,
      setCoverageFilter: (v) => setCoverageFilter(normalizeFilter(v)),
      setJurisdictionFilter: (v) => setJurisdictionFilter(normalizeFilter(v)),
      setAuthorityFilter: (v) => setAuthorityFilter(normalizeFilter(v)),
      setLegislativeStatusFilter: (v) => setLegislativeStatusFilter(normalizeFilter(v)),
      setActivityFilter: (v) => setActivityFilter(normalizeFilter(v)),
      setAppliesToFilter: (v) => setAppliesToFilter(normalizeFilter(v)),
      setLifecycleStageFilter: (v) => setLifecycleStageFilter(normalizeFilter(v)),
      setTechnicalScopeFilter: (v) => setTechnicalScopeFilter(normalizeFilter(v)),
      setRiskSubdomainFilter: (v) => setRiskSubdomainFilter(normalizeFilter(v)),
      setRiskDomainFilter: (v) => setRiskDomainFilter(normalizeFilter(v)),
      setSectorFilter: (v) => setSectorFilter(normalizeFilter(v)),
      setViewMode,
      setActorRole,
      resetFilters: () => {
        setStackBy(config.defaultStackBy);
        setCoverageFilter([]);
        setJurisdictionFilter([]);
        setAuthorityFilter([]);
        setLegislativeStatusFilter([]);
        setActivityFilter([]);
        setAppliesToFilter([]);
        setLifecycleStageFilter([]);
        setTechnicalScopeFilter([]);
        setRiskSubdomainFilter([]);
        setRiskDomainFilter([]);
        setSectorFilter([]);
        setActorRole(DEFAULT_ACTOR_ROLE);
      },
    };
    chartRegistry.set(containerId, controller);
    return () => { chartRegistry.delete(containerId); };
  }, [containerId, config.defaultStackBy]);

  // Detect mobile and dark mode
  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    updateMobile();
    window.addEventListener('resize', updateMobile);

    const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)');
    const updateDarkMode = () => setIsDarkMode(darkModeMedia.matches);
    if (typeof darkModeMedia.addEventListener === 'function') {
      darkModeMedia.addEventListener('change', updateDarkMode);
    }

    return () => {
      window.removeEventListener('resize', updateMobile);
      if (typeof darkModeMedia.removeEventListener === 'function') {
        darkModeMedia.removeEventListener('change', updateDarkMode);
      }
    };
  }, []);

  // Fetch chart data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams();
    params.set('stackBy', stackBy);
    if (config.aggregation !== 'risk-subdomain') {
      params.set('aggregation', config.aggregation);
    }
    if (config.aggregation === 'actors') {
      params.set('actorRole', actorRole);
    }
    if (coverageFilter.length) params.set('coverage', coverageFilter.join(','));
    if (jurisdictionFilter.length) params.set('jurisdiction', jurisdictionFilter.join(','));
    if (authorityFilter.length) params.set('authority', authorityFilter.join(','));
    if (legislativeStatusFilter.length) params.set('legislativeStatus', legislativeStatusFilter.join(','));
    if (activityFilter.length) params.set('activity', activityFilter.join(','));
    if (appliesToFilter.length) params.set('appliesTo', appliesToFilter.join(','));
    if (lifecycleStageFilter.length) params.set('lifecycleStage', lifecycleStageFilter.join(','));
    if (technicalScopeFilter.length) params.set('technicalScope', technicalScopeFilter.join(','));
    if (riskSubdomainFilter.length) params.set('riskSubdomain', riskSubdomainFilter.join(','));
    if (riskDomainFilter.length) params.set('riskDomain', riskDomainFilter.join(','));
    if (sectorFilter.length) params.set('sector', sectorFilter.join(','));

    fetch(`${apiBase}/data/risk-subdomain?${params}`)
      .then((r) => r.json())
      .then((json: ApiResponse) => {
        if (cancelled) return;
        setData(json);

        // Update filter options
        if (json.jurisdictions) {
          setJurisdictions(json.jurisdictions.map((v) => ({ value: v, label: v })));
        }
        if (json.authorities) {
          setAuthorities(json.authorities.map((v) => ({ value: v, label: v })));
        }
        if (json.legislativeStatuses) {
          setLegislativeStatuses(json.legislativeStatuses.map((v) => ({ value: v, label: v })));
        }
        if (json.activities) {
          setActivities(json.activities.map((v) => ({ value: v, label: v })));
        }
        if (json.appliesToOptions) {
          setAppliesToOptions(json.appliesToOptions.map((v: string) => ({ value: v, label: v })));
        }
        if (json.lifecycleStageOptions) {
          setLifecycleStageOptions(json.lifecycleStageOptions.map((v: string) => ({ value: v, label: v })));
        }
        if (json.technicalScopeOptions) {
          setTechnicalScopeOptions(json.technicalScopeOptions.map((v: string) => ({ value: v, label: v })));
        }
      })
      .catch((err) => {
        console.error('Failed to fetch chart data:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, config.aggregation, stackBy, coverageFilter.join(','), jurisdictionFilter.join(','), authorityFilter.join(','), legislativeStatusFilter.join(','), activityFilter.join(','), appliesToFilter.join(','), lifecycleStageFilter.join(','), technicalScopeFilter.join(','), riskSubdomainFilter.join(','), riskDomainFilter.join(','), sectorFilter.join(','), actorRole]);

  // Initialize and update chart
  useEffect(() => {
    if (!chartRef.current || !data) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;
    const { seriesCategories, data: rows, totalDocumentsAll } = data;

    // For risk subdomains, use taxonomy names; for others, use title from API
    const labels = config.aggregation === 'risk-subdomain'
      ? rows.map((d) => getSubdomainName(d.id) ?? d.title)
      : rows.map((d) => d.title);
    const ids = rows.map((d) => d.id);

    const isProportionView = viewMode === 'proportion';
    const toPercent = (v: number) =>
      totalDocumentsAll > 0 ? (v / totalDocumentsAll) * 100 : 0;

    const chartTheme = getChartTheme(isDarkMode);
    const inheritedColor = getComputedStyle(chartRef.current.parentElement ?? chartRef.current).color || chartTheme.text;
    const containerWidth = chartRef.current.clientWidth;
    const responsiveConfig = getResponsiveConfig(containerWidth);
    const currentIsMobile = containerWidth < MOBILE_BREAKPOINT;
    const exportMode = isExportMode();

    const {
      labelWidth: baseWidth,
      gridLeft: baseGridLeft,
      maxLabelLength: baseMaxLabel,
      axisLabelSize: baseAxisSize = 10,
      axisLabelLineHeight: baseAxisLineHeight = 12,
      axisLabelMargin: baseAxisMargin = 12,
    } = responsiveConfig;

    // Export mode: larger text and wider label area
    // Risk subdomain gets 25% larger than the default export size
    // Charts with short labels (legislative status, actors) use narrower left margin
    const isRiskSubdomain = config.aggregation === 'risk-subdomain';
    const hasShortLabels = config.aggregation === 'legislativeStatus' || config.aggregation === 'allActors' || config.aggregation === 'actors';
    const labelWidth = exportMode
      ? (isRiskSubdomain ? 400 : hasShortLabels ? 220 : 380)
      : baseWidth;
    const gridLeft = exportMode
      ? (isRiskSubdomain ? 430 : hasShortLabels ? 250 : 410)
      : baseGridLeft;
    const maxLabelLength = exportMode
      ? (isRiskSubdomain ? 42 : hasShortLabels ? 30 : 52)
      : baseMaxLabel;
    const axisLabelSize = exportMode ? (isRiskSubdomain ? 19 : 15) : baseAxisSize;
    const axisLabelLineHeight = exportMode ? (isRiskSubdomain ? 20 : 19) : baseAxisLineHeight;
    const axisLabelMargin = exportMode ? (isRiskSubdomain ? 20 : 16) : baseAxisMargin;

    const gridBottom = currentIsMobile ? 30 : 30;

    // Resolve bar colour for a series
    const getSeriesColor = (category: string, index: number): string => {
      if (config.singleBarColor && stackBy === 'none') return config.singleBarColor;
      if (config.customColorMap?.[category]) return config.customColorMap[category];
      if (stackBy === 'coverage') return getCoverageColor(category);
      if (stackBy === 'actorRole') return getActorRoleColor(category);
      return getCategoryColor(index);
    };

    // Build series
    const isGrouped = config.grouped && seriesCategories.length > 1 && stackBy !== 'none';
    const series = seriesCategories.map((category, index) => ({
      name: category,
      type: 'bar' as const,
      ...(isGrouped ? {} : { stack: 'total' }),
      data: rows.map((d) =>
        isProportionView ? toPercent(d.values[index] ?? 0) : d.values[index] ?? 0
      ),
      barCategoryGap: '6.5px',
      itemStyle: {
        color: getSeriesColor(category, index),
        borderRadius: isGrouped ? [0, 3, 3, 0] : (index === seriesCategories.length - 1 ? [0, 3, 3, 0] : 0),
      },
    }));

    // Calculate dynamic height — grouped bars need more vertical space per category
    const baseRowHeight = exportMode ? (isRiskSubdomain ? 45 : EXPORT_ROW_HEIGHT) : ROW_HEIGHT;
    const effectiveRowHeight = isGrouped ? baseRowHeight * Math.min(seriesCategories.length, 4) * 0.6 : baseRowHeight;
    const chartHeight = Math.max(300, rows.length * effectiveRowHeight + 180);
    chartRef.current.style.height = `${chartHeight}px`;
    chart.resize();

    // Calculate max value for axis
    // For grouped bars, max is the single largest value; for stacked, it's the sum
    const maxStackValue = rows.reduce((max, row) => {
      if (isGrouped) {
        const maxVal = Math.max(...row.values.map((v) => isProportionView ? toPercent(v) : v));
        return Math.max(max, maxVal);
      }
      const total = row.values.reduce(
        (sum, v) => sum + (isProportionView ? toPercent(v) : v),
        0
      );
      return Math.max(max, total);
    }, 0);
    const niceCeil = niceMax(maxStackValue);
    const axisMax = Math.max(1, isProportionView ? Math.min(niceCeil, 100) : niceCeil);

    // Helper functions for label formatting
    const truncateLabel = (label: string, max: number): string => {
      if (label.length <= max) return label;
      return label.slice(0, max - 1) + '…';
    };

    const splitLabel = (label: string, max: number): string => {
      const trimmed = label.trim();
      if (trimmed.length <= max) return trimmed;

      const midpoint = Math.floor(trimmed.length / 2);
      let bestIndex = -1;
      let bestDistance = Infinity;

      for (let i = 0; i < trimmed.length; i++) {
        if (trimmed[i] !== ' ') continue;
        const distance = Math.abs(i - midpoint);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = i;
        }
      }

      if (bestIndex <= 0) return truncateLabel(trimmed, max);

      const firstLine = trimmed.slice(0, bestIndex).trim();
      const secondLine = trimmed.slice(bestIndex + 1).trim();
      return `${firstLine}\n${secondLine}`;
    };

    const formatMobileLabel = (label: string): string => {
      const mobileMaxLength = maxLabelLength;
      const trimmed = label.trim();
      if (trimmed.length <= mobileMaxLength) return trimmed;

      const breakIndex = trimmed.lastIndexOf(' ', mobileMaxLength);
      const firstLine = breakIndex > 0
        ? trimmed.slice(0, breakIndex)
        : trimmed.slice(0, mobileMaxLength);
      const remainder = trimmed
        .slice(breakIndex > 0 ? breakIndex + 1 : mobileMaxLength)
        .trimStart();

      if (!remainder) return firstLine;

      const secondLine = remainder.slice(0, mobileMaxLength);
      const suffix = remainder.length > mobileMaxLength ? '...' : '';
      return `${firstLine}\n${secondLine}${suffix}`;
    };

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        show: !isMobile,
        trigger: 'axis',
        triggerOn: isMobile ? 'none' : 'mousemove|click',
        axisPointer: isMobile ? { type: 'none' } : { type: 'shadow' },
        backgroundColor: chartTheme.tooltipBg,
        borderColor: chartTheme.tooltipBorder,
        textStyle: { color: chartTheme.tooltipText },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            dataIndex: number;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          const idx = paramsArray?.[0]?.dataIndex ?? 0;
          const header = config.tooltipHeaderFormat === 'id-label'
            ? `${ids[idx]} - ${labels[idx]}`
            : labels[idx];
          const lines = paramsArray
            .map(
              (p) =>
                `${p.marker}${p.seriesName}: ${
                  isProportionView ? p.value.toFixed(1) + '%' : Math.round(p.value)
                }`
            )
            .join('<br/>');
          return `${header}<br/>${lines}`;
        },
      },
      legend: { show: false, data: seriesCategories },
      grid: {
        left: gridLeft,
        right: 30,
        top: 30,
        bottom: gridBottom,
      },
      graphic: [],
      xAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        axisLabel: {
          formatter: (value: number) =>
            isProportionView ? `${value}%` : `${Math.round(value)}`,
          color: inheritedColor,
          ...(exportMode ? { fontSize: isRiskSubdomain ? 21 : 14 } : {}),
        },
        axisLine: { lineStyle: { color: chartTheme.grid } },
        axisTick: { lineStyle: { color: chartTheme.grid } },
        splitLine: { lineStyle: { color: chartTheme.grid } },
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        triggerEvent: true,
        axisTick: {
          alignWithLabel: true,
          lineStyle: { color: chartTheme.grid },
        },
        axisLine: { lineStyle: { color: chartTheme.grid } },
        ...(exportMode && isGrouped ? {
          splitArea: {
            show: true,
            areaStyle: {
              color: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.03)'],
            },
          },
          splitLine: {
            show: true,
            lineStyle: { color: chartTheme.grid, width: 1 },
          },
        } : {}),
        axisLabel: {
          width: labelWidth,
          fontSize: axisLabelSize,
          lineHeight: axisLabelLineHeight,
          align: 'right',
          verticalAlign: 'middle',
          margin: axisLabelMargin,
          formatter: (value: string) =>
            currentIsMobile ? formatMobileLabel(value) : splitLabel(value, maxLabelLength),
          color: inheritedColor,
        },
      },
      series,
      animationDuration: theme.chart.animationDuration,
    };

    chart.setOption(option, { notMerge: true });

    // Handle click/tap events. bindChartTap also routes touch taps in via
    // `mouseup`, because zrender silently drops a `click` whose finger
    // drifted over 4px or rested past 300ms — see src/lib/chartTap.ts.
    tapCleanupRef.current?.();
    tapCleanupRef.current = bindChartTap(chart, chartRef.current!, (raw) => {
      const params = raw as echarts.ECElementEvent;
      if (isMobileRef.current) {
        // On mobile: show summary modal instead of going straight to document list
        const { dataIndex, componentType } = params;
        if (componentType === 'yAxis' || dataIndex === undefined) return;

        const rowId = ids[dataIndex];
        const rowLabel = labels[dataIndex];
        if (!rowId || !rowLabel) return;

        const title = config.tooltipHeaderFormat === 'id-label'
          ? `${rowId} - ${rowLabel}`
          : rowLabel;

        // Build series breakdown for this bar
        const seriesData: SummarySeriesItem[] = seriesCategories
          .map((cat, i) => {
            const rawValue = rows[dataIndex]?.values?.[i] ?? 0;
            const color = config.customColorMap?.[cat]
              ?? (stackBy === 'coverage' ? getCoverageColor(cat)
                : stackBy === 'actorRole' ? getActorRoleColor(cat)
                : getCategoryColor(i));
            return { name: cat, value: rawValue, rawCount: rawValue, color };
          });

        setSummaryModalTitle(title);
        setSummaryModalSeries(seriesData);
        setSummaryModalOpen(true);
        pendingClickRef.current = { params, ids, labels };
      } else {
        handleChartClick(params, ids, labels);
      }
    });

    // Handle y-axis label hover for description tooltips (risk subdomains only, desktop only)
    chart.off('mouseover');
    chart.off('mouseout');
    if (config.hasYAxisDescriptions && !isMobile) {
      chart.on('mouseover', (params: unknown) => {
        const p = params as { componentType?: string; value?: string; event?: { event?: MouseEvent } };
        if (p.componentType === 'yAxis' && tooltipRef.current) {
          const row = rows.find((d) => (getSubdomainName(d.id) ?? d.title) === p.value);
          if (row) {
            const description = getSubdomainDescription(row.id);
            if (description) {
              const name = getSubdomainName(row.id) ?? row.title;
              tooltipRef.current.innerHTML = `<strong>${name}</strong><br/>${description}`;
              tooltipRef.current.style.display = 'block';
              const mouseEvent = p.event?.event;
              if (mouseEvent) {
                const el = tooltipRef.current;
                const tooltipWidth = el.offsetWidth;
                const tooltipHeight = el.offsetHeight;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                let left = mouseEvent.clientX + 15;
                let top = mouseEvent.clientY - 10;
                if (left + tooltipWidth > viewportWidth - 10) {
                  left = mouseEvent.clientX - tooltipWidth - 15;
                }
                if (top + tooltipHeight > viewportHeight - 10) {
                  top = viewportHeight - tooltipHeight - 10;
                }
                if (top < 10) top = 10;
                if (left < 10) left = 10;
                el.style.left = `${left}px`;
                el.style.top = `${top}px`;
              }
            }
          }
        }
      });
      chart.on('mouseout', (params: unknown) => {
        const p = params as { componentType?: string };
        if (p.componentType === 'yAxis' && tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      });
    }

    return () => {
      tapCleanupRef.current?.();
      tapCleanupRef.current = null;
      chart.off('mouseover');
      chart.off('mouseout');
    };
  }, [data, viewMode, isDarkMode, stackBy, theme, config, isMobile]);

  // Handle chart click
  const handleChartClick = useCallback((params: echarts.ECElementEvent, ids: string[], labels: string[]) => {
    const { dataIndex, seriesName, componentType } = params;

    if (componentType === 'yAxis') return;

    // Handle bar click - open document list modal
    if (dataIndex !== undefined && seriesName && data) {
      const rowId = ids[dataIndex];
      const rowLabel = labels[dataIndex];
      if (!rowId || !rowLabel) return;

      // Build query params for details API
      const qs = new URLSearchParams();
      qs.set('filterType', config.detailsFilterType);
      const detailsParam = DETAILS_FILTER_PARAMS[config.detailsFilterType];
      qs.set(detailsParam, rowId);
      qs.set('stackBy', stackBy);
      if (config.detailsFilterType === 'actors') {
        qs.set('actorRole', actorRole);
      }

      if (stackBy === 'coverage') {
        const coverage = COVERAGE_BY_SERIES[seriesName as string];
        if (!coverage) return;
        qs.set('coverage', String(coverage));
      } else {
        qs.set('category', seriesName as string);
      }

      if (jurisdictionFilter.length) qs.set('jurisdiction', jurisdictionFilter.join(','));
      if (authorityFilter.length) qs.set('authority', authorityFilter.join(','));
      if (legislativeStatusFilter.length) qs.set('legislativeStatus', legislativeStatusFilter.join(','));
      if (activityFilter.length) qs.set('activity', activityFilter.join(','));
      if (coverageFilter.length) qs.set('coverageFilter', coverageFilter.join(','));
      if (appliesToFilter.length) qs.set('appliesTo', appliesToFilter.join(','));
      if (lifecycleStageFilter.length) qs.set('lifecycleStage', lifecycleStageFilter.join(','));
      if (technicalScopeFilter.length) qs.set('technicalScope', technicalScopeFilter.join(','));
      if (riskSubdomainFilter.length) qs.set('riskSubdomain', riskSubdomainFilter.join(','));
      if (riskDomainFilter.length) qs.set('riskDomain', riskDomainFilter.join(','));
      if (sectorFilter.length) qs.set('sector', sectorFilter.join(','));

      // Build active filter chips for display
      const COVERAGE_VALUE_LABELS: Record<string, string> = { '3': 'Good', '2': 'Minimal', '1': 'No coverage' };
      const STACK_BY_LABEL: Record<string, string> = {
        coverage: 'Coverage',
        jurisdiction: 'Jurisdiction',
        authority: 'Authority',
        legislativeStatus: 'Legislative Status',
        activity: 'Activity',
      };
      const filters: ActiveFilter[] = [];
      filters.push({ label: STACK_BY_LABEL[stackBy] || stackBy, values: [seriesName as string] });
      if (stackBy !== 'coverage' && coverageFilter.length) filters.push({ label: 'Coverage', values: coverageFilter.map(v => COVERAGE_VALUE_LABELS[v] || v) });
      if (stackBy !== 'jurisdiction' && jurisdictionFilter.length) filters.push({ label: 'Jurisdiction', values: [...jurisdictionFilter] });
      if (stackBy !== 'authority' && authorityFilter.length) filters.push({ label: 'Authority', values: [...authorityFilter] });
      if (stackBy !== 'legislativeStatus' && legislativeStatusFilter.length) filters.push({ label: 'Legislative Status', values: [...legislativeStatusFilter] });
      if (stackBy !== 'activity' && activityFilter.length) filters.push({ label: 'Activity', values: [...activityFilter] });
      if (appliesToFilter.length) filters.push({ label: 'Applies To', values: [...appliesToFilter] });
      if (lifecycleStageFilter.length) filters.push({ label: 'Lifecycle Stage', values: [...lifecycleStageFilter] });
      if (technicalScopeFilter.length) filters.push({ label: 'Technical Scope', values: [...technicalScopeFilter] });
      if (riskSubdomainFilter.length) filters.push({ label: 'Risk Subdomain', values: riskSubdomainFilter.map(id => getSubdomainName(id) ?? id) });
      if (riskDomainFilter.length) filters.push({ label: 'Risk Domain', values: riskDomainFilter.map(id => RISK_DOMAINS.find(d => d.id === id)?.label ?? id) });
      if (sectorFilter.length) filters.push({ label: 'Sector', values: sectorFilter.map(id => SECTOR_FIELDS.find(sf => sf.id === id)?.label ?? id) });

      // Title: for risk subdomains use "id - label", for others just the label
      const modalTitle = config.tooltipHeaderFormat === 'id-label'
        ? `${rowId} - ${rowLabel}`
        : rowLabel;

      setListModalTitle(modalTitle);
      setListModalFilters(filters);
      setListModalOpen(true);
      setListModalLoading(true);
      setListModalItems([]);

      fetch(`${apiBase}/data/risk-subdomain/details?${qs}`)
        .then((r) => r.json())
        .then((json: { items?: DocumentListItem[] }) => {
          setListModalItems(json?.items ?? []);
        })
        .catch((err) => {
          console.error('Failed to fetch document list:', err);
        })
        .finally(() => {
          setListModalLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, config, stackBy, jurisdictionFilter.join(','), authorityFilter.join(','), legislativeStatusFilter.join(','), activityFilter.join(','), coverageFilter.join(','), appliesToFilter.join(','), lifecycleStageFilter.join(','), technicalScopeFilter.join(','), riskSubdomainFilter.join(','), riskDomainFilter.join(','), sectorFilter.join(','), actorRole, data]);

  // Handle "View Document List" from mobile summary modal
  const handleViewDocuments = useCallback(() => {
    setSummaryModalOpen(false);
    if (pendingClickRef.current) {
      handleChartClick(pendingClickRef.current.params, pendingClickRef.current.ids, pendingClickRef.current.labels);
      pendingClickRef.current = null;
    }
  }, [handleChartClick]);

  const openDetail = useCallback((documentId: string) => {
    setDetailDocumentId(documentId);
    setDetailModalOpen(true);
    setDetailModalLoading(true);
    setDetailDocument(null);

    fetch(`${apiBase}/data/risk-subdomain/document?documentId=${encodeURIComponent(documentId)}`)
      .then((r) => r.json())
      .then((json: { document?: DocumentDetail }) => {
        setDetailDocument(json?.document ?? null);
      })
      .catch((err) => {
        console.error('Failed to fetch document details:', err);
      })
      .finally(() => {
        setDetailModalLoading(false);
      });
  }, [apiBase]);

  // The detail modal is mirrored in the URL (?document=<id>) as on Document
  // View; a pasted URL opens the detail without the list. See lib/modalUrl.
  const closeDetail = useCallback(() => setDetailModalOpen(false), []);
  const docUrl = useModalUrl(documentUrl, containerId, openDetail, closeDetail);

  // Handle document click in list modal
  const handleDocumentClick = useCallback((documentId: string) => {
    openDetail(documentId);
    docUrl.push(documentId);
  }, [openDetail, docUrl.push]);

  const handleDetailClose = useCallback(() => {
    closeDetail();
    docUrl.clear();
  }, [closeDetail, docUrl.clear]);

  // Resize chart on window resize
  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const wrapperTheme = getChartTheme(isDarkMode);

  const exportModeRender = isExportMode();

  // Legend (hidden in export mode for single-colour charts). Rendered
  // above the chart interactively, below it in export mode.
  const showLegend = !!data && !(exportModeRender && stackBy === 'none');
  const renderLegend = () => data && (
        <div style={{
          gap: 8,
          // Interactive: the legend sits above the chart, next to the
          // controls, so both are visible without scrolling. Export mode
          // keeps it beneath the chart so rendered images keep their crop.
          marginTop: exportModeRender ? 4 : 0,
          marginBottom: exportModeRender ? 0 : 8,
          padding: '0 4px',
        }}>
          <div>
            {/* Same heading shape as the incident charts' LegendHeader:
                bold title on its own line, hint beneath it. Export mode
                keeps its larger title size and drops the hint, since the
                rendered image has nothing to click. */}
            <div style={{ marginBottom: 6 }}>
              <div style={{
                fontSize: exportModeRender ? (config.aggregation === 'risk-subdomain' ? 19 : 15) : 13,
                fontWeight: 700,
                color: 'var(--gc-foreground, inherit)',
                lineHeight: 1.3,
              }}>
                {STACK_BY_LABELS[stackBy]}
              </div>
              {!exportModeRender && (
                <div style={{ fontSize: 10, fontStyle: 'italic', opacity: 0.75, marginTop: 3, color: 'var(--gc-text-muted, #6b7280)' }}>
                  Click categories to toggle visibility
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {data.seriesCategories.map((cat, i) => {
                const isHidden = hiddenSeries.has(cat);
                const color = config.customColorMap?.[cat] ?? (stackBy === 'coverage' ? getCoverageColor(cat) : stackBy === 'actorRole' ? getActorRoleColor(cat) : getCategoryColor(i));
                return (
                  <div
                    key={cat}
                    onClick={() => {
                      const next = new Set(hiddenSeries);
                      if (next.has(cat)) {
                        next.delete(cat);
                      } else {
                        next.add(cat);
                      }
                      setHiddenSeries(next);
                      if (chartInstance.current) {
                        chartInstance.current.dispatchAction({
                          type: 'legendToggleSelect',
                          name: cat,
                        });
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (stackBy === 'coverage' && COVERAGE_LEGEND_TOOLTIPS[cat] && tooltipRef.current) {
                        const desc = COVERAGE_LEGEND_TOOLTIPS[cat];
                        const colonIdx = desc.indexOf(':');
                        const description = colonIdx !== -1 ? desc.slice(colonIdx + 1).trim() : '';
                        tooltipRef.current.innerHTML = `<strong>${cat}</strong>${description ? '<br/>' + description : ''}`;
                        tooltipRef.current.style.display = 'block';
                        const mouseEvent = e as unknown as MouseEvent;
                        const el = tooltipRef.current;
                        const tooltipWidth = el.offsetWidth;
                        const tooltipHeight = el.offsetHeight;
                        const vw = window.innerWidth;
                        const vh = window.innerHeight;
                        let left = mouseEvent.clientX + 15;
                        let top = mouseEvent.clientY - 10;
                        if (left + tooltipWidth > vw - 10) left = mouseEvent.clientX - tooltipWidth - 15;
                        if (top + tooltipHeight > vh - 10) top = vh - tooltipHeight - 10;
                        if (top < 10) top = 10;
                        if (left < 10) left = 10;
                        el.style.left = `${left}px`;
                        el.style.top = `${top}px`;
                      }
                    }}
                    onMouseLeave={() => {
                      if (tooltipRef.current) {
                        tooltipRef.current.style.display = 'none';
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: exportModeRender ? (config.aggregation === 'risk-subdomain' ? 9 : 7) : 5,
                      cursor: exportModeRender ? 'default' : 'pointer',
                      opacity: isHidden ? 0.35 : 1,
                      fontSize: exportModeRender ? (config.aggregation === 'risk-subdomain' ? 19 : 15) : 12,
                      color: 'var(--gc-foreground, inherit)',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: exportModeRender ? (config.aggregation === 'risk-subdomain' ? 22 : 18) : 14,
                      height: exportModeRender ? (config.aggregation === 'risk-subdomain' ? 22 : 18) : 14,
                      borderRadius: 2,
                      background: color,
                      flexShrink: 0,
                    }} />
                    {cat}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
  );

  return (
    <div style={{ position: 'relative', background: 'transparent', color: 'inherit' }}>
      {!exportModeRender && (() => {
        // Build the dimensions list passed to IncidentFilterBar. Each entry
        // becomes either an inactive Add-filter menu item (selected empty) or
        // a chip on the bar (selected populated). config.hiddenFilters hides
        // dims whose dimension is the chart's y-axis. Coverage is governed
        // by config.showCoverageFilter — counts/lifecycle/etc. charts don't
        // expose a level-of-coverage filter at all.
        const filterDims: FilterDim[] = [];
        if (config.showCoverageFilter) {
          filterDims.push({ key: 'coverage', label: 'Coverage', options: COVERAGE_OPTIONS, selected: coverageFilter, onChange: setCoverageFilter });
        }
        if (!config.hiddenFilters.includes('jurisdiction')) {
          filterDims.push({ key: 'jurisdiction', label: 'Jurisdiction', options: jurisdictions, selected: jurisdictionFilter, onChange: setJurisdictionFilter });
        }
        if (!config.hiddenFilters.includes('authority')) {
          filterDims.push({ key: 'authority', label: 'Authority', options: authorities, selected: authorityFilter, onChange: setAuthorityFilter });
        }
        if (!config.hiddenFilters.includes('legislativeStatus')) {
          filterDims.push({ key: 'legislativeStatus', label: 'Legislative status', options: legislativeStatuses, selected: legislativeStatusFilter, onChange: setLegislativeStatusFilter });
        }
        if (!config.hiddenFilters.includes('activity')) {
          filterDims.push({ key: 'activity', label: 'Most recent activity', options: activities, selected: activityFilter, onChange: setActivityFilter });
        }
        if (!config.hiddenFilters.includes('appliesTo')) {
          filterDims.push({ key: 'appliesTo', label: 'Primarily applies to', options: appliesToOptions, selected: appliesToFilter, onChange: setAppliesToFilter });
        }
        if (!config.hiddenFilters.includes('lifecycleStage')) {
          filterDims.push({ key: 'lifecycleStage', label: 'Lifecycle stage', options: lifecycleStageOptions, selected: lifecycleStageFilter, onChange: setLifecycleStageFilter });
        }
        if (!config.hiddenFilters.includes('technicalScope')) {
          filterDims.push({ key: 'technicalScope', label: 'Technical scope', options: technicalScopeOptions, selected: technicalScopeFilter, onChange: setTechnicalScopeFilter });
        }
        if (!config.hiddenFilters.includes('riskDomain')) {
          filterDims.push({ key: 'riskDomain', label: 'Risk domain', options: RISK_DOMAIN_OPTIONS, selected: riskDomainFilter, onChange: setRiskDomainFilter, tooltip: 'Filter for documents with Good Coverage on any subdomain' });
        }
        if (!config.hiddenFilters.includes('riskSubdomain')) {
          filterDims.push({ key: 'riskSubdomain', label: 'Risk subdomain', options: RISK_SUBDOMAIN_OPTIONS, selected: riskSubdomainFilter, onChange: setRiskSubdomainFilter, tooltip: 'Filter for documents with Good Coverage' });
        }
        if (!config.hiddenFilters.includes('sector')) {
          filterDims.push({ key: 'sector', label: 'Sector', options: SECTOR_OPTIONS, selected: sectorFilter, onChange: setSectorFilter, tooltip: 'Filter for documents with Good Coverage' });
        }

        const hasActiveFilters =
          stackBy !== config.defaultStackBy ||
          [coverageFilter, jurisdictionFilter, authorityFilter, legislativeStatusFilter,
            activityFilter, appliesToFilter, lifecycleStageFilter, technicalScopeFilter,
            riskSubdomainFilter, riskDomainFilter, sectorFilter].some((f) => f.length > 0) ||
          (config.aggregation === 'actors' && actorRole !== DEFAULT_ACTOR_ROLE);

        const handleStackByChange = (next: StackByOption | null) => {
          // Treat "None" (null) as the chart's default — Governance charts
          // always render with some stacking dimension.
          const resolved: StackByOption = next ?? 'none';
          setStackBy(resolved);
          // Auto-fill coverage filter to Good Coverage when switching to a
          // non-coverage stacking dimension. Preserves the FilterControls
          // behaviour that kept the bars meaningful after a stack switch.
          if (resolved !== 'coverage' && config.showCoverageFilter && coverageFilter.length === 0) {
            setCoverageFilter(['3']);
          }
        };

        const handleReset = () => {
          setStackBy(config.defaultStackBy);
          setCoverageFilter([]);
          setJurisdictionFilter([]);
          setAuthorityFilter([]);
          setLegislativeStatusFilter([]);
          setActivityFilter([]);
          setAppliesToFilter([]);
          setLifecycleStageFilter([]);
          setTechnicalScopeFilter([]);
          setRiskSubdomainFilter([]);
          setRiskDomainFilter([]);
          setSectorFilter([]);
          setActorRole(DEFAULT_ACTOR_ROLE);
        };

        const showActorRole = config.aggregation === 'actors' && config.stackByOptions.length > 0;

        return (
          <div style={{ marginBottom: 10 }}>
            {/* Chart title */}
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--gc-foreground, inherit)',
              textAlign: 'center',
              marginBottom: 8,
            }}>
              {config.title}
            </div>

            {/* Actor role sub-selector (actors chart with stack options only) */}
            {showActorRole && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
                marginBottom: 6,
                fontSize: 11,
              }}>
                <span style={{ color: 'var(--gc-text-muted, #6b7280)' }}>Role:</span>
                {ACTOR_ROLES.map((role) => {
                  const active = actorRole === role.id;
                  return (
                    <button
                      key={role.id}
                      type="button"
                      onClick={active ? undefined : () => setActorRole(role.id as ActorRole)}
                      title={active ? `Showing ${role.label}` : `Switch to ${role.label}`}
                      style={{
                        border: active
                          ? '1px solid var(--gc-button-bg-active, #e5e7eb)'
                          : '1px solid var(--gc-control-border, #d1d5db)',
                        background: active ? 'var(--gc-button-bg-active, #e5e7eb)' : 'transparent',
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: active ? 600 : 400,
                        color: active ? 'var(--gc-button-text-active, #111827)' : 'var(--gc-text-muted, #6b7280)',
                        cursor: active ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                        borderRadius: 9,
                        lineHeight: '14px',
                      }}
                    >
                      {role.label}
                    </button>
                  );
                })}
              </div>
            )}

            <IncidentFilterBar<StackByOption>
              availableStackBy={config.stackByOptions}
              stackBy={stackBy === 'none' ? null : stackBy}
              onStackByChange={handleStackByChange}
              stackByLabels={STACK_BY_DISPLAY}
              allowNoneStackBy={false}
              stackByControlLabel="Stack by"
              dimensions={filterDims}
              severityFields={[]}
              severityFilters={{}}
              onSeverityChange={() => { /* no severity dimension on Gov Map */ }}
              hasActiveFilter={hasActiveFilters}
              onResetAll={handleReset}
              isMobile={isMobile}
            />
          </div>
        );
      })()}

      {showLegend && !exportModeRender && renderLegend()}

      <div style={{ position: 'relative' }}>
        <div
          ref={chartRef}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            minHeight: 300,
          }}
        />

        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            padding: '8px 12px',
            background: 'rgba(50, 50, 50, 0.95)',
            color: '#fff',
            borderRadius: '4px',
            fontSize: '12px',
            lineHeight: '1.5',
            maxWidth: '350px',
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            wordWrap: 'break-word',
            textAlign: 'left',
          }}
        />

        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(128,128,128,0.15)',
            }}
          >
            Loading...
          </div>
        )}

      </div>

      {/* X-axis label row: centered label + right-aligned view toggle */}
      {data && !exportModeRender && (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'baseline',
          justifyContent: isMobile ? 'center' : 'flex-start',
          position: 'relative',
          marginTop: 0,
          padding: '0 4px',
          gap: isMobile ? 2 : 0,
          minHeight: 20,
          // Matches the incident charts: keep clear of whatever the host
          // page stacks below the chart.
          marginBottom: 32,
        }}>
          {isMobile ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--gc-foreground, inherit)', textAlign: 'center' }}>
                {viewMode === 'proportion' ? 'Proportion of documents' : 'Document counts'}
              </div>
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isMobile={isMobile}
              />
            </>
          ) : (
            <>
              <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 12, color: 'var(--gc-foreground, inherit)', pointerEvents: 'none' }}>
                {viewMode === 'proportion' ? 'Proportion of documents' : 'Document counts'}
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <ViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isMobile={isMobile}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Export mode: x-axis label only (no toggle) */}
      {data && exportModeRender && (
        <div style={{ textAlign: 'center', fontSize: config.aggregation === 'risk-subdomain' ? 19 : 15, color: 'var(--gc-foreground, inherit)', marginTop: 4, padding: '0 4px' }}>
          {viewMode === 'proportion' ? 'Proportion of documents' : 'Document counts'}
        </div>
      )}

      {showLegend && exportModeRender && renderLegend()}

      <DocumentListModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title={listModalTitle}
        items={listModalItems}
        loading={listModalLoading}
        onDocumentClick={handleDocumentClick}
        activeFilters={listModalFilters}
      />

      <DocumentDetailModal
        isOpen={detailModalOpen}
        onClose={handleDetailClose}
        documentId={detailDocumentId}
        document={detailDocument}
        loading={detailModalLoading}
      />

      <SummaryModal
        isOpen={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        title={summaryModalTitle}
        seriesData={summaryModalSeries}
        totalDocuments={data?.totalDocumentsAll ?? 0}
        onViewDocuments={handleViewDocuments}
      />
    </div>
  );
}
